import { useEffect, useState } from "react";
import { Blog } from "@/types";
import { HtmlTextEditor } from "../components/ui/HtmlTextEditor";

function AddBlogForm({
  onSubmit,
  onCancel,
  initialBlog,
  loading = false
}: {
  onSubmit: (blog: Blog) => void | Promise<void>;
  onCancel: () => void;
  initialBlog?: Blog | null;
  loading?: boolean;
}) {

  const [title, setTitle] = useState(initialBlog?.title ?? "");
  const [excerpt, setExcerpt] = useState(initialBlog?.excerpt ?? "");
  const [content, setContent] = useState(initialBlog?.content ?? "");
  
  const [featured, setFeatured] = useState(initialBlog?.featured ?? false);

  const [imagePreview, setImagePreview] = useState<string | null>(
    initialBlog?.image ?? null
  );
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);

  const [imageAlt, setImageAlt] = useState(
    (() => {
      const raw = initialBlog?.image_alt ?? "";
      const text = String(raw).trim();
      return text.toLowerCase() === "null" || text.toLowerCase() === "undefined" ? "" : text;
    })()
  );

  const [status, setStatus] = useState<"draft" | "published">(
    initialBlog?.status ?? "published"
  );

  const [metaTitle, setMetaTitle] = useState(
    initialBlog?.meta_title ?? ""
  );

  const [metaDescription, setMetaDescription] = useState(
    initialBlog?.meta_description ?? ""
  );

  const [metaKeywords, setMetaKeywords] = useState(
    initialBlog?.meta_keywords?.join(", ") ?? ""
  );

  const [error, setError] = useState<string | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }

        // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    setError(null);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = () => {
    setError(null);

    // Validate required fields
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    if (!excerpt.trim()) {
      setError("Short description is required");
      return;
    }

    if (!content.trim()) {
      setError("Content is required");
      return;
    }

    if (!imagePreview) {
      setError("Please upload a cover image");
      return;
    }

    if (!imageAlt.trim()) {
      setError("Image alt text is required");
      return;
    }

    const newBlog: Blog = {
      id: initialBlog?.id ?? Date.now(),
      slug: title.toLowerCase().replace(/\s+/g, "-"),
      title,
      excerpt,
      content,
      author: initialBlog?.author ?? "Admin",
      date: initialBlog?.date ?? new Date().toDateString(),
      readTime: initialBlog?.readTime ?? "3 min read",
      image: imagePreview,
      imageFile,
      image_alt: imageAlt.trim(),
      featured,

      status,
      lastUpdated: new Date().toISOString(),

      meta_title: metaTitle.trim() || title,
      meta_description: metaDescription.trim() || excerpt,
      meta_keywords: metaKeywords
        ? metaKeywords.split(",").map(k => k.trim()).filter(Boolean)
        : undefined
    };

    onSubmit(newBlog);
  };


  useEffect(() => {
    if (!initialBlog) {
      setTitle("");
      setExcerpt("");
      setContent("");
      setFeatured(false);
      setImagePreview(null);
      setImageFile(undefined);
      setImageAlt("");
      setStatus("published");
      setMetaTitle("");
      setMetaDescription("");
      setMetaKeywords("");
      return;
    }

    setTitle(initialBlog.title ?? "");
    setExcerpt(initialBlog.excerpt ?? "");
    setContent(initialBlog.content ?? "");
    setFeatured(!!initialBlog.featured);
    setImagePreview(initialBlog.image ?? null);
    setImageFile(undefined);
    const rawAlt = initialBlog.image_alt ?? "";
    const normalizedAlt = String(rawAlt).trim();
    setImageAlt(
      normalizedAlt.toLowerCase() === "null" || normalizedAlt.toLowerCase() === "undefined"
        ? ""
        : normalizedAlt
    );
    setStatus(initialBlog.status ?? "published");
    setMetaTitle(initialBlog.meta_title ?? "");
    setMetaDescription(initialBlog.meta_description ?? "");
    setMetaKeywords(
      initialBlog.meta_keywords?.join(", ") ?? ""
    );
  }, [initialBlog]);


return (
  <div className="space-y-6 bg-background text-foreground">

    {/* Error Message */}
    {error && (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
        {error}
      </div>
    )}

    {/* Image Upload */}
    <div>
      <label className="block text-sm font-medium mb-2">
        Blog cover image
      </label>

      <div className="relative border-2 border-dashed rounded-xl p-4 bg-muted/40 dark:bg-muted">
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />

        {imagePreview ? (
          <img
            src={imagePreview}
            alt={imageAlt || "Blog image preview"}
            className="h-44 w-full object-cover rounded-lg"
          />
        ) : (
          <div className="h-44 flex items-center justify-center text-sm text-muted-foreground">
            Click to upload image
          </div>
        )}
      </div>
    </div>

    {/* Image Alt */}
    <div>
      <label className="block text-sm font-medium mb-1">
        Image alt text
      </label>
      <input
        placeholder="Describe the blog image"
        value={imageAlt}
        onChange={e => setImageAlt(e.target.value)}
        className="w-full border rounded-lg px-4 py-2
                   bg-background text-foreground
                   placeholder:text-muted-foreground
                   focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </div>

    {/* Title */}
    <input
      placeholder="Blog title"
      value={title}
      onChange={e => setTitle(e.target.value)}
      className="w-full border rounded-lg px-4 py-2
                 bg-background text-foreground
                 placeholder:text-muted-foreground
                 focus:outline-none focus:ring-2 focus:ring-primary"
    />

    {/* Excerpt */}
    <input
      placeholder="Short description"
      value={excerpt}
      onChange={e => setExcerpt(e.target.value)}
      className="w-full border rounded-lg px-4 py-2
                 bg-background text-foreground
                 placeholder:text-muted-foreground
                 focus:outline-none focus:ring-2 focus:ring-primary"
    />

  

    {/* Content */}
    <HtmlTextEditor
      value={content}
      onChange={setContent}
    />

    {/* Featured */}
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={featured}
        onChange={e => setFeatured(e.target.checked)}
      />
      <span className="text-sm">Featured blog</span>
    </div>

    {/* Status */}
    <div>
      <label className="block text-sm font-medium mb-1">
        Status
      </label>
      <select
        value={status}
        onChange={e => setStatus(e.target.value as "draft" | "published")}
        className="w-full border rounded-lg px-4 py-2
                   bg-background text-foreground
                   focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="draft">Draft</option>
        <option value="published">Published</option>
      </select>
    </div>

    {/* Meta Details */}
    <div className="border rounded-xl p-4 bg-muted/50 dark:bg-muted space-y-3">
      <h4 className="text-sm font-semibold">
        Meta Data (Optional)
      </h4>

      <input
        placeholder="Meta title (defaults to blog title)"
        value={metaTitle}
        onChange={e => setMetaTitle(e.target.value)}
        className="w-full border rounded-lg px-4 py-2
                   bg-background text-foreground
                   placeholder:text-muted-foreground
                   focus:outline-none focus:ring-2 focus:ring-primary"
      />

      <textarea
        placeholder="Meta description (defaults to short description)"
        value={metaDescription}
        onChange={e => setMetaDescription(e.target.value)}
        className="w-full border rounded-lg px-4 py-2 min-h-[80px]
                   bg-background text-foreground
                   placeholder:text-muted-foreground
                   focus:outline-none focus:ring-2 focus:ring-primary"
      />

      <input
        placeholder="Meta keywords (comma separated)"
        value={metaKeywords}
        onChange={e => setMetaKeywords(e.target.value)}
        className="w-full border rounded-lg px-4 py-2
                   bg-background text-foreground
                   placeholder:text-muted-foreground
                   focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </div>

    {/* Actions */}
    <div className="flex justify-end gap-3 pt-4 border-t">
      <button
        onClick={onCancel}
        disabled={loading}
        className="px-4 py-2 border rounded-lg
                   bg-background text-foreground
                   disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Cancel
      </button>

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="px-5 py-2 bg-blue-600 text-white rounded-lg
                   disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading
          ? "Saving..."
          : initialBlog
          ? "Update Blog"
          : "Publish Blog"}
      </button>
    </div>

  </div>
);

}

export default AddBlogForm;
